<template>
  <template v-if="arePremiumFeaturesAvailable">
    <NewResumeTypeMenu v-if="newResumeType === 'START'" @set-resume-type="i => (newResumeType = i)" />

    <ImportResume v-else-if="newResumeType === 'IMPORT'" :sorted-templates="sortedTemplates" />
  </template>

  <div
    v-if="newResumeType === 'FROM_SCRATCH' || !arePremiumFeaturesAvailable"
    class="templates-popup"
    :class="{ overflow: !templateIdSelected }"
  >
    <div class="header">
      <div class="title">{{ $t('popUp.createResume.title') }}</div>
    </div>

    <div class="templates-container" :class="{ big: !templateIdSelected }">
      <div v-show="!templateIdSelected" class="section">
        <div class="title-container">
          <div class="title">{{ $t('popUp.createResume.template') }}</div>
        </div>
        <div class="templates">
          <div
            v-for="template in sortedTemplates"
            :key="template.id"
            class="template"
            :style="`background-image: url(${template.thumbnailUrl})`"
            @click="
              templateIdSelected = template.id;
              selectedTemplateUrl = template.thumbnailUrl;
            "
          ></div>
        </div>
      </div>
      <div v-show="templateIdSelected" class="section create">
        <div class="selectedTemplate" :style="`background-image: url(${selectedTemplateUrl})`">
          <check class="icon" />
        </div>
        <div class="container">
          <div class="title-container">
            <div class="title">{{ $t('popUp.createResume.infos') }}</div>
          </div>

          <div v-if="user.organization && user.organization.name" class="field checkboxs">
            <div class="checkbox-container" @click="isOrganization = true">
              <div class="checkbox" :class="{ checked: isOrganization }"></div>
              <div class="title">{{ user.organization.name }}</div>
            </div>

            <div class="checkbox-container" @click="isOrganization = false">
              <div class="checkbox" :class="{ checked: !isOrganization }"></div>
              <div class="title">{{ $t('popUp.createResume.personalGroup') }}</div>
            </div>
          </div>

          <div class="field">
            <div class="title">{{ $t('popUp.createResume.firstname') }}*</div>
            <input v-model="firstname" placeholder="John" type="text" class="text-input opacity" />
          </div>

          <div class="field">
            <div class="title">{{ $t('popUp.createResume.lastname') }}</div>
            <input v-model="lastname" placeholder="Doe" type="text" class="text-input opacity" />
          </div>

          <div class="field">
            <div class="title">{{ $t('popUp.createResume.jobTitle') }}*</div>

            <input
              v-model="jobTitle"
              :placeholder="$t('popUp.createResume.jobPlaceHolder')"
              type="text"
              class="text-input opacity"
            />
          </div>

          <div class="field">
            <div class="title-group">
              <div class="title">{{ $t('popUp.createResume.labels') }}</div>

              <div v-if="user.organization && user.organization.name" class="help-icon-container">
                <HelpIcon class="help-icon" />
                <div class="info">{{ $t('popUp.createResume.labelsHelp') }}</div>
              </div>
            </div>

            <ResumeLabelsSelect v-model="labels" :user="user" :is-organization="isOrganization" />
          </div>

          <div class="field infos">
            {{ $t('popUp.createResume.mandatoryField') }}
          </div>

          <div class="center">
            <div class="main-button button-cancel" @click="templateIdSelected = null">
              <div class="text">{{ $t('popUp.createResume.previous') }}</div>
            </div>

            <div class="main-button button-create" :class="{ disabled: !canCreate }" @click="createResume">
              <div class="text">{{ $t('popUp.createResume.createButton') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import check from '@/assets/icons/checked.svg';
import NewResumeTypeMenu from './CreateResumePopup/NewResumeTypeMenu.vue';
import ImportResume from './CreateResumePopup/ImportResume.vue';
import HelpIcon from '@/assets/icons/help.svg';
import ResumeLabelsSelect from '@/components/ResumeLabelsSelect.vue';

export default {
  name: 'CreatePopup',

  components: {
    HelpIcon,
    check,
    NewResumeTypeMenu,
    ImportResume,
    ResumeLabelsSelect,
  },

  data: function () {
    return {
      isOrganization: false,
      firstname: '',
      lastname: '',
      labels: [],
      jobTitle: '',
      referenceLink: '',
      templateIdSelected: null,
      selectedTemplateUrl: null,
      contactId: null,
      newResumeType: 'START',
      labelsFromSearch: [],
    };
  },

  computed: {
    ...mapGetters(['templates', 'contacts', 'user', 'popupResolve', 'popupReject']),
    arePremiumFeaturesAvailable() {
      return this.user.organization?.premiumFeaturesAvailable;
    },
    canCreate() {
      return this.firstname && this.jobTitle && this.templateIdSelected;
    },
    sortedTemplates() {
      return [...this.templates].sort((a, b) => b.type - a.type);
    },
  },

  watch: {
    isOrganization(newValue) {
      this.labels = this.labels.filter(label => label.fromOrganization === newValue || !label.id);
    },
  },

  mounted() {
    if (this.user.organization) {
      this.isOrganization = true;
    }
  },

  methods: {
    async createResume() {
      if (!this.canCreate) return;
      const candidate = {
        firstname: this.firstname,
        lastname: this.lastname,
        jobTitle: this.jobTitle,
        referenceLink: this.referenceLink,
      };
      const template = this.templates.find(t => t.id === this.templateIdSelected);
      try {
        const resume = await this.$store.dispatch('resumes.create', {
          candidate,
          template,
          contactId: this.contactId,
          labels: this.labels,
          isOrganization: this.isOrganization,
        });
        await this.popupResolve(resume);
      } catch (e) {
        console.error(e);
        if (e.response.status === 400) {
          alert('too much character');
        } else if (e.response.status === 429) {
          alert('quota exceeded');
        } else {
          alert(e.response.data);
        }
        await this.popupResolve(false);
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/inputs.scss';
@use '@/styles/buttons.scss';

.overflow {
  overflow: hidden;
}

.templates-popup {
  max-height: 600px;
  width: 800px;
  max-width: 800px;
  box-sizing: border-box;

  .header {
    border-bottom: 1px solid colors.$light-blue-color;
    width: 100%;
    .title {
      color: colors.$light-blue-color;
      padding-bottom: 22px;
      font-size: 16px;
      font-weight: bold;
      text-transform: uppercase;
    }
  }
  .templates-container {
    display: flex;
    flex-wrap: wrap;
    height: 480px;
    box-sizing: border-box;
    transition: all 0.1s;

    &.big {
      height: 500px;
    }

    .section {
      padding-top: 27px;
      display: flex;
      flex-direction: column;
      position: relative;
      box-sizing: border-box;
      height: 100%;
      width: 100%;

      &.create {
        padding: 22px 0 0;
        margin: 0;
        flex-direction: row;
        justify-content: space-between;
        align-items: flex-start;
        .selectedTemplate {
          width: 272px;
          height: 385px;
          border-radius: 4px;
          background-position: center;
          background-size: contain;
          background-repeat: no-repeat;
          box-shadow:
            0 2px 8px -2px rgba(0, 0, 0, 0.1),
            0 4px 12px -4px rgba(0, 0, 0, 0.2);
          border: solid 1px colors.$light-blue-color;
          position: relative;

          .icon {
            width: 13px;
            height: 13px;
            color: #fff;
            position: absolute;
            top: 0;
            right: 0;
            background: colors.$light-blue-color;
            padding: 6px;
            border-radius: 4px;
            border-top-right-radius: 0;
            border-top-left-radius: 0;
            border-bottom-right-radius: 0;
          }
        }
      }
      .container {
        display: flex;
        align-items: flex-start;
        justify-content: flex-start;
        flex-direction: column;
        flex-grow: 1;
        padding-left: 59px;
        margin-left: 59px;
        border-left: 1px solid #d0d0d0;
        .center {
          width: 100%;
          flex-grow: 1;
          display: flex;
          flex-direction: row;
          align-items: flex-start;
          justify-content: center;
          box-sizing: border-box;

          .main-button {
            margin: 50px 0 0 0;
            &:first-child {
              margin-right: 22px;
            }
          }
        }
      }
      .field {
        width: 100%;
        margin: 6px 0;
        max-width: 350px;

        .title-group {
          display: flex;
          align-items: center;
          justify-content: space-between;

          .help-icon-container {
            position: relative;
            width: 20px;
            height: 20px;
            cursor: pointer;

            .help-icon {
              height: 100%;
              width: 100%;
              color: #333333;
              transition: opacity 0.2s;

              &:hover {
                opacity: 0.8;
              }
            }

            &:hover .info {
              scale: 1;
              pointer-events: auto;
            }

            .info {
              position: absolute;
              background-color: #fff;
              box-shadow: 0 1px 4px 0 rgba(75, 75, 75, 0.5);
              border: 2px solid rgba(45, 100, 112, 1);
              border-radius: 8px;
              padding: 9px;
              right: -14px;
              top: 150%;
              text-align: center;
              line-height: 0.8rem;
              width: 10.5rem;
              transform-origin: 160px top;
              scale: 0;
              pointer-events: none;
              transition: all 0.2s ease-in-out;
              user-select: none;
              font-size: 12px;
              z-index: 99;

              &::after {
                content: '';
                position: absolute;
                background-color: white;
                rotate: 45deg;
                top: -7.5px;
                right: 16px;
                width: 10px;
                height: 10px;
                border-left: 2px solid rgba(45, 100, 112, 1);
                border-top: 2px solid rgba(45, 100, 112, 1);
              }
            }
          }
        }

        .title {
          height: 19px;
          color: #939292;
          font-size: 16px;
          margin: 0 0 4px;
        }

        input {
          &::placeholder {
            opacity: 0.5;
            font-family: Lato, sans-serif;
          }
        }

        &.checkboxs {
          display: flex;
          align-items: center;
          .checkbox-container {
            display: flex;
            align-items: center;
            margin-right: 24px;
            cursor: pointer;
            .title {
              height: 19px;
              color: #333333;
              font-size: 16px;
              margin-left: 8px;
            }
          }
        }

        &.infos {
          height: 15px;
          width: 100%;
          color: colors.$light-blue-color;
          font-size: 12px;
          font-style: italic;
          margin: 0 0 30px;
          text-align: right;
          max-width: 350px;
        }
      }
      > div:first-child:nth-last-child(1) {
        margin-bottom: 12px;
      }
      .templates {
        display: flex;
        flex-wrap: wrap;
        gap: 22px;
        justify-content: flex-start;
        height: 100%;
        width: 100%;
        box-sizing: border-box;
        overflow: scroll;
        margin: 0;
        padding: 1px;
        .template {
          box-sizing: border-box;
          width: 230px;
          height: 325px;
          border-radius: 4px;
          margin-bottom: 40px;
          background-position: center;
          background-size: contain;
          background-repeat: no-repeat;
          box-shadow:
            0 2px 8px -2px rgba(0, 0, 0, 0.1),
            0 4px 12px -4px rgba(0, 0, 0, 0.2);
          border: solid 1px white;
          cursor: pointer;
          &:hover {
            border-color: colors.$light-blue-color;
          }
        }
      }
      .title-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin: 0 0 12px 0;
        .arrow {
          position: absolute;
          left: 0;
        }

        .title {
          color: #000000;
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 4px;
        }
      }
      .validate {
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }
}
</style>
